import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { resolveFaIcon } from 'helpers/faIcons';
import CgSpinner from './CgSpinner';
import { cn } from 'modules/Common/utils/cn';

/**
 * Button component with customizable size, variant, and icon.
 *
 * @param {Object} props - The component props.
 * @param {Function} [props.onClick] - Function to be called on button click. Defaults to an empty function.
 * @param {string} [props.size="md"] - Size of the button. Can be "xs", "sm", "md", or "lg". Defaults to "md".
 * @param {string} [props.label="Button"] - Text label for the button. Defaults to "Button".
 * @param {string} [props.variant="info"] - Variant of the button. Can be "primary", "secondary", "info", "danger", or "premium". Defaults to "info".
 * @param {string} [props.href] - URL for the button to act as a link. If provided, renders an `<a>` element.
 * @param {string} [props.icon] - FontAwesome icon name to display inside the button.
 * @param {boolean} [props.outlined=false] - Whether the button should use outlined styles. Defaults to false.
 * @param {string} [props.className] - Additional CSS classes for the button.
 * @param {boolean} [props.isToolbarButton=false] - Whether the button is a toolbar button. Defaults to false.
 * @param {boolean} [props.loading=false] - Whether the button is in a loading state. Defaults to false.
 * @param {string} [props.loadingText] - Text to show while loading. If not provided, uses the button label.

 * @param {string} [props.title] - Tooltip text to display on hover.
 * 
 * @returns {JSX.Element} The rendered button or link element.
 */
const CgButton = React.forwardRef(
  (
    {
      onClick = () => {},
      size = 'md',
      label,
      variant = 'info',
      href,
      icon,
      outlined = false,
      disabled = false,
      className = '',
      isToolbarButton = false,
      loading = false,
      loadingText,
      title,
      id,
      type = 'button',
      children,
      ...rest
    },
    ref,
  ) => {
    const forwarded = Object.fromEntries(
      Object.entries(rest).filter(([key]) => key.startsWith('data-') || key.startsWith('aria-')),
    );

    const sizeClasses = {
      xs: 'text-[10px] px-2 py-0.5',
      sm: 'text-xs px-3 py-1',
      md: 'text-sm px-4 py-2',
      lg: 'text-lg px-6 py-3',
    };

    const spinnerSizes = {
      sm: 'w-3 h-3',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
    };

    const variantClasses = {
      primary: 'bg-primaryColor hover:bg-primaryColor-dark text-white border-2 border-transparent',
      secondary: 'bg-gray-100 hover:bg-gray-200 text-secondaryColor border-2 border-transparent',
      info: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-transparent',
      danger: 'bg-red-500 hover:bg-red-700 text-white border-2 border-transparent',
      premium:
        'bg-gradient-to-r from-orange-400 to-orange-600 text-white hover:from-orange-500 hover:to-orange-700 border-2 border-transparent',
    };

    const outlinedClasses = {
      primary: 'border-primaryColor text-primaryColor hover:bg-primaryColor hover:text-white',
      secondary: 'border-secondaryColor text-secondaryColor hover:bg-gray-100',
      info: 'border-gray-200 text-gray-700 hover:bg-gray-100',
      danger: 'border-red-500 text-red-500 hover:bg-red-500 hover:text-white',
      premium:
        'border-[3px] hover:bg-gradient-to-r hover:from-orange-400 hover:to-orange-600 border-orange-500 hover:border-orange-400 bg-clip-border text-orange-500 from-orange-500 to-orange-700 hover:bg-clip-padding hover:text-white',
    };

    const buttonBaseClass = `${sizeClasses[size]} font-semibold transition-colors duration-300 ease-in-out flex items-center justify-center ${!isToolbarButton ? 'rounded-lg shadow-md' : ''}`;
    const variantClass = loading
      ? outlinedClasses[variant] // Always use outlined style when loading
      : outlined
        ? outlinedClasses[variant]
        : variantClasses[variant];
    const borderClass = outlined || loading ? 'border-2' : '';
    const disabledClass = disabled || loading ? 'opacity-50 cursor-not-allowed' : '';

    // Check if className contains border styles to avoid overriding them
    const hasBorderClasses = className.includes('border-') || className.includes('!border');

    const toolbarClass = isToolbarButton
      ? `!rounded-none !shadow-none hover:!bg-gray-100 ${hasBorderClasses ? '' : '!border-0'} h-full !bg-transparent`
      : '';
    // cn() so a caller utility (e.g. rounded-full) beats the base one it
    // conflicts with, instead of losing on stylesheet order.
    const buttonClass = cn(
      buttonBaseClass,
      variantClass,
      borderClass,
      disabledClass,
      toolbarClass,
      className,
      'relative',
    );

    const renderSpinner = () => {
      if (!loading) return null;

      // Use the variant color for the spinner (the colors Bootstrap's Spinner
      // variants resolved to under the old $primary/$danger overrides).
      const spinnerVariant = variant === 'info' ? 'primary' : variant;
      const spinnerColors = {
        primary: 'text-primaryColor',
        secondary: 'text-secondaryColor',
        danger: 'text-dangerColor',
      };
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <CgSpinner
            size="sm"
            className={`${spinnerSizes[size]} ${spinnerColors[spinnerVariant] || ''}`}
          />
        </div>
      );
    };

    const renderIcon = () => {
      if (!icon || loading) return null;

      let iconElement;
      if (typeof icon === 'string') {
        // Guarded lookup — a broken icon getter must not crash the button.
        const resolved = resolveFaIcon(icon);
        if (!resolved) return null;
        iconElement = (
          <FontAwesomeIcon
            size={size === 'sm' ? 'sm' : 'lg'}
            icon={resolved}
          />
        );
      } else {
        iconElement = icon;
      }

      return (
        <span className={`text-current ${label && !isToolbarButton ? 'mr-2' : ''}`}>
          {iconElement}
        </span>
      );
    };

    const renderContent = () => {
      if (isToolbarButton) {
        return (
          <div className="flex flex-col items-center gap-0.5">
            {children ? (
              <div className={`${loading ? 'opacity-0' : ''}`}>{children}</div>
            ) : (
              <>
                {renderIcon()}
                {label && (
                  <span className={`text-xs whitespace-nowrap ${loading ? 'opacity-0' : ''}`}>
                    {loading ? loadingText || label : label}
                  </span>
                )}
              </>
            )}
            {renderSpinner()}
          </div>
        );
      }
      return (
        <>
          {renderIcon()}
          {children ? (
            <span className={loading ? 'opacity-0' : ''}>{children}</span>
          ) : (
            <span className={loading ? 'opacity-0' : ''}>
              {loading ? loadingText || label : label}
            </span>
          )}
          {renderSpinner()}
        </>
      );
    };

    if (href) {
      return (
        <a
          {...forwarded}
          ref={ref}
          href={href}
          onClick={loading ? undefined : onClick}
          className={buttonClass}
          style={{ pointerEvents: loading ? 'none' : undefined }}
          title={title}
          id={id}
        >
          {renderContent()}
        </a>
      );
    }

    return (
      <button
        {...forwarded}
        ref={ref}
        type={type}
        onClick={loading ? undefined : onClick}
        className={buttonClass}
        disabled={disabled || loading}
        title={title}
        id={id}
      >
        {renderContent()}
      </button>
    );
  },
);

CgButton.displayName = 'CgButton';

CgButton.propTypes = {
  onClick: PropTypes.func,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  label: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary', 'info', 'danger', 'premium']),
  href: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  outlined: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  isToolbarButton: PropTypes.bool,
  loading: PropTypes.bool,
  loadingText: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  children: PropTypes.node,
};

export default CgButton;
